import { useCallback } from 'react';
import { isEmpty, noop } from 'lodash-es';

import { VehicleModel } from 'client/data/models/vehicle-v2';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import { isEqualById } from 'site-modules/shared/utils/appraiser/compare-utils';
import { LOCATION_AND_STYLE } from 'site-modules/shared/constants/appraisal/appraisal';
import { getPredefinedVin } from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-utils';
import {
  getDefaultState,
  validateAndUpdateStep,
} from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-step-processor';
import { isLocationAndStyleStepValid } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-validators';

/**
 * Encapsulates all vehicle-related state reads and writes (make/model/year/style).
 *
 * @param {Object} params
 * @param {Object} params.state - current appraiser state
 * @param {Function} params.setState - state setter
 * @param {Object} params.location - current page location
 * @param {boolean} params.isExtendedReport - whether this is the extended partner-offer flow
 * @param {Object} params.mileageRef - ref to the mileage input (for reset on make change)
 * @param {Object} params.pendingCallbacksRef - ref queue for post-commit callbacks
 * @param {Function} params.setModelValue - luckdragon model setter
 */
export function useVehicleData({
  state,
  setState,
  location,
  isExtendedReport,
  mileageRef,
  pendingCallbacksRef,
  setModelValue,
}) {
  const { vehicle } = state;

  const getVehicleData = useCallback(() => {
    return {
      makeName: vehicle?.make?.name ?? '',
      modelName: vehicle?.model?.name ?? '',
      makeSlug: vehicle?.style?.makeSlug ?? '',
      modelSlug: vehicle?.style?.modelSlug ?? '',
      styleName: vehicle?.style?.name ?? '',
      styleId: vehicle?.style?.id ?? '',
      type: vehicle?.style?.submodels?.name ?? '',
      year: vehicle?.style?.year ?? null,
      publicationState: vehicle?.style?.publicationState ?? '',
    };
  }, [vehicle]);

  const getVehicleStyle = useCallback(() => vehicle?.style?.slug || null, [vehicle?.style?.slug]);

  const getVehicleYear = useCallback(() => vehicle?.year?.year || null, [vehicle?.year?.year]);

  const getVehicleMMY = useCallback(
    () => ({
      make: vehicle?.make?.name ?? '',
      model: vehicle?.model?.name ?? '',
      year: vehicle?.year?.year ?? null,
    }),
    [vehicle]
  );

  const setGeneralVehicleData = useCallback(
    (make, model, year, style, isSetVehicleDataForExtendedReport, callback = noop) => {
      delayHeavyTask(() => {
        if (callback !== noop) {
          pendingCallbacksRef.current.push(callback);
        }

        setState(prev => {
          const { vehicle: prevVehicle, exteriorColor, interiorColor, options } = prev;
          let clearedState = {};

          if (make && prevVehicle.make && !isEqualById(make, prevVehicle.make) && !isSetVehicleDataForExtendedReport) {
            const clearedBase = getDefaultState({
              zipCode: prev.zipCode,
              isZipCodeValid: prev.isZipCodeValid,
              selectedStyle: prevVehicle.style,
              errorMap: prev.errorMap,
              location,
              isExtendedReport,
              isVinFromDrawer: prev.isVinFromDrawer,
              predefinedVin: getPredefinedVin(location),
              isOfferConditionsStepLoaded: prev.isOfferConditionsStepLoaded,
              isLicensePlateAndVinStepLoaded: prev.isLicensePlateAndVinStepLoaded,
              isInstantOfferStepLoaded: prev.isInstantOfferStepLoaded,
            });

            if (mileageRef.current?.reset) {
              mileageRef.current.reset();
            }

            clearedState = isExtendedReport
              ? {
                  ...clearedBase,
                  isPartnerOfferEligibleVin: prev.isPartnerOfferEligibleVin,
                  partnerQuestionnaireVerboseResponse: prev.partnerQuestionnaireVerboseResponse,
                  partnerQuestionnaireAnswers: prev.partnerQuestionnaireAnswers,
                  areQuestionnaireResponsesValid: prev.areQuestionnaireResponsesValid,
                  vin: prev.vin,
                  isEscapeHatch: prev.isEscapeHatch,
                  isAllowedToPredefineVin: prev.isAllowedToPredefineVin,
                  emailFromEmailStep: prev.emailFromEmailStep,
                }
              : clearedBase;
          }

          const resolvedStyle =
            isSetVehicleDataForExtendedReport && style === null && prevVehicle.style?.id ? prevVehicle.style : style;
          const vehicleObj = { make, model, year, style: resolvedStyle };
          const isStyleChanged = !isEqualById(prevVehicle.style, resolvedStyle);
          let stateUpdate = {};

          if (style?.id && isStyleChanged) {
            stateUpdate = {
              isLoadingColorsAndOptions: true,
              exteriorColor: {},
              interiorColor: {},
              options: [],
              prefilledExteriorColorId: exteriorColor?.id,
              prefilledInteriorColorId: interiorColor?.id,
              prefilledOptions: options,
              isStyleFromStyleMismatchModal: false,
            };
          }

          const newState = { ...prev, ...clearedState, vehicle: vehicleObj, ...stateUpdate };

          return validateAndUpdateStep(newState, LOCATION_AND_STYLE, isLocationAndStyleStepValid, isExtendedReport, {
            resetSubsequent: isStyleChanged,
          });
        });
      });
    },
    [setState, pendingCallbacksRef, location, isExtendedReport, mileageRef]
  );

  const setNewStyle = useCallback(
    (style, isStyleFromStyleMismatchModal) => {
      setState(prev => ({
        ...prev,
        vehicle: { ...prev.vehicle, style },
        isStyleFromStyleMismatchModal,
        isLoadingColorsAndOptions: true,
      }));
    },
    [setState]
  );

  const setAdditionalStepsData = useCallback(
    (data, callback) => {
      if (!isEmpty(data)) {
        setState(prev => ({ ...prev, ...data }));
        if (data.vin) {
          setModelValue('estimatedAppraisalVin', VehicleModel, data.vin);
        }
        if (typeof callback === 'function') {
          callback();
        }
      }
    },
    [setModelValue, setState]
  );

  return {
    getVehicleData,
    getVehicleStyle,
    getVehicleYear,
    getVehicleMMY,
    setGeneralVehicleData,
    setNewStyle,
    setAdditionalStepsData,
  };
}
